import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { FileUpload, ItemTemplateOptions } from 'primereact/fileupload'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { ConfigurableComponent } from '../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../ui-config'
import { FileUploadItemTemplate } from './template'

type FormFieldName = 'attachment' | 'description'

type Props = {
  formik: FormikProps<any>
  isFormFieldValid: (name: FormFieldName) => boolean
  getFormErrorMessage: (name: FormFieldName) => false | JSX.Element
}

export const SectionForm: React.FC<Props> = ({
  formik,
  isFormFieldValid,
  getFormErrorMessage,
}) => {
  const { t } = useTranslation('eInvoiceDetails')
  const { uiConfig: config } = useConfigContext()

  // attachment
  const attachmentLabel = (
    <label
      htmlFor="attachment"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('attachment'),
      })}
    ></label>
  )

  const uploadOptions = {
    icon: '',
    iconOnly: true,
    className: 'hidden',
  }
  const cancelOptions = {
    icon: '',
    iconOnly: true,
    className: 'hidden',
  }

  const onTemplateRemove = (callback: Function) => {
    formik.setFieldValue('attachment', null)
    callback()
  }

  const itemTemplate = (fileObject: any, props: ItemTemplateOptions) => {
    return (
      <FileUploadItemTemplate
        fileObject={fileObject}
        props={props}
        onTemplateRemove={onTemplateRemove}
      />
    )
  }

  const attachmentComponent = (
    <FileUpload
      id="attachment"
      uploadOptions={uploadOptions}
      cancelOptions={cancelOptions}
      itemTemplate={itemTemplate}
      emptyTemplate={t(
        'stepper.additionalDocuments.table.upload.dialog.form.attachment.emptyMessage',
      )}
      onRemove={(event: any) => formik.setFieldValue('attachment', null)}
      onSelect={(event: any) =>
        formik.setFieldValue('attachment', event.files[0])
      }
      invalidFileSizeMessageSummary={t(
        'stepper.additionalDocuments.table.upload.dialog.form.attachment.validation.invalidFileSize.summary',
      )}
      invalidFileSizeMessageDetail={t(
        'stepper.additionalDocuments.table.upload.dialog.form.attachment.validation.invalidFileSize.detail',
      )}
    />
  )

  const attachmentErrorMsg = <div>{getFormErrorMessage('attachment')}</div>

  const attachmentWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalDocuments}
      component={attachmentComponent}
      label={attachmentLabel}
      errorMsg={attachmentErrorMsg}
    />
  )

  // description
  const descriptionLabel = (
    <label
      htmlFor="description"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('description'),
      })}
    ></label>
  )

  const descriptionComponent = (
    <InputText
      id="description"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('description'),
      })}
      value={formik.values.description}
      onChange={formik.handleChange}
    />
  )

  const descriptionErrorMsg = <div>{getFormErrorMessage('description')}</div>

  const descriptionWrapper = (
    <ConfigurableComponent
      config={config.eInvoice.additionalDocuments}
      component={descriptionComponent}
      label={descriptionLabel}
      errorMsg={descriptionErrorMsg}
    />
  )
  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="formgrid grid">
        <div className="col">{attachmentWrapper}</div>
      </div>

      <div className="formgrid grid">
        <div className="col">{descriptionWrapper}</div>
      </div>
    </form>
  )
}
